import React, { useState, useEffect, useRef } from 'react';
import { Button, Spin, Empty, notification } from 'antd';
import { DownloadOutlined, BarChartOutlined, DollarCircleOutlined, FilterOutlined } from '@ant-design/icons';
import { useDispatch, useSelector } from 'react-redux';
import { getUserRole, rejectedApiMessage } from '../utilities/CommonHelper';
import { getUserAccessData } from '../utilities/sessionHelper';
import { fetchForecastRevenue, fetchFilterOptions, fetchSalesViewRevenue, loadMoreForecastRevenue, loadMoreSalesViewRevenue, exportFinanceViewAsExcel, exportSalesViewAsExcel } from '../store/reducers/revenueForecastSlice';
import { AppDispatch, RootState } from '../store/store';
import FilterDrawer from '../containers/RevenueForecasting/FilterDrawer';
import FinanceView from '../containers/RevenueForecasting/FinanceViewNew';
import SalesView from '../containers/RevenueForecasting/SalesViewNew';
import '../containers/RevenueForecasting/RevenueForecasting.css';

const RevenueForecaste: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>();
  
  // Get current fiscal quarter and year
  const getCurrentFiscalQuarter = () => {
    const month = new Date().getMonth() + 1; // 0-indexed, so add 1
    // Fiscal year: April-June = Q1, July-Sep = Q2, Oct-Dec = Q3, Jan-Mar = Q4
    if (month >= 4 && month <= 6) return 1;
    if (month >= 7 && month <= 9) return 2;
    if (month >= 10 && month <= 12) return 3;
    return 4; // Jan-Mar
  };
  
  const getCurrentFiscalYear = () => {
    const month = new Date().getMonth() + 1;
    const year = new Date().getFullYear();
    // If month is Jan-Mar (Q4), fiscal year is previous calendar year
    // If month is Apr-Dec (Q1-Q3), fiscal year is current calendar year
    return month >= 4 ? year : year - 1;
  };
  
  const currentYear = getCurrentFiscalYear();
  const currentQuarter = getCurrentFiscalQuarter();
  
  // Get data from Redux store
  const { 
    data: forecastData, 
    loading, 
    error,
    loadingMore,
    currentPage,
    salesData, 
    salesLoading, 
    salesError,
    salesLoadingMore,
    salesCurrentPage,
    filterOptions, 
    filterOptionsLoading,
    exportLoading 
  } = useSelector((state: RootState) => state.revenueForecast);
  
  // Get user role
  const userRole = getUserRole(getUserAccessData()?.roleAccessMap) || '';
  
  // Check if user is admin or superuser
  const isAdminOrSuperuser = userRole.toLowerCase().includes('admin') || userRole.toLowerCase().includes('superuser');
  
  // Determine which views are accessible based on role
  const isFinanceRole = userRole.toLowerCase().includes('finance') || userRole.toLowerCase().includes('admin') || userRole.toLowerCase().includes('superuser');
  const isSalesRole = userRole.toLowerCase().includes('sales') || userRole.toLowerCase().includes('admin') || userRole.toLowerCase().includes('superuser');
  
  // Default to finance view if both are accessible, otherwise use the available one
  const [activeView, setActiveView] = useState<'finance' | 'sales'>(
    isFinanceRole ? 'finance' : 'sales'
  );

  // Finance View Filter states
  const [financeSelectedBU, setFinanceSelectedBU] = useState<string[]>([]);
  const [financeSelectedClient, setFinanceSelectedClient] = useState<string[]>([]);
  const [financeSelectedDepartment, setFinanceSelectedDepartment] = useState<string[]>([]);
  const [financeStartYear, setFinanceStartYear] = useState<number>(currentYear);
  const [financeStartQuarter, setFinanceStartQuarter] = useState<number>(currentQuarter);
  const [financeEndYear, setFinanceEndYear] = useState<number>(currentYear);
  const [financeEndQuarter, setFinanceEndQuarter] = useState<number>(currentQuarter);
  const [financePage, setFinancePage] = useState<number>(1);
  const [financePageSize, setFinancePageSize] = useState<number>(10);

  // Sales View Filter states
  const [salesSelectedBU, setSalesSelectedBU] = useState<string[]>([]);
  const [salesSelectedClient, setSalesSelectedClient] = useState<string[]>([]);
  const [salesSelectedDepartment, setSalesSelectedDepartment] = useState<string[]>([]);
  const [salesStartYear, setSalesStartYear] = useState<number>(currentYear);
  const [salesStartQuarter, setSalesStartQuarter] = useState<number>(currentQuarter);
  const [salesEndYear, setSalesEndYear] = useState<number>(currentYear);
  const [salesEndQuarter, setSalesEndQuarter] = useState<number>(currentQuarter);
  const [salesPage, setSalesPage] = useState<number>(1);
  const [salesPageSize, setSalesPageSize] = useState<number>(10);

  // Track if last search was done with all BUs and Clients (empty arrays)
  const [financeShowConsolidated, setFinanceShowConsolidated] = useState<boolean>(true);
  const [salesShowConsolidated, setSalesShowConsolidated] = useState<boolean>(true);

  // Filter drawer state
  const [filterDrawerVisible, setFilterDrawerVisible] = useState<boolean>(false);

  // Refs to track initial load
  const financeInitialLoadDone = useRef<boolean>(false);
  const salesInitialLoadDone = useRef<boolean>(false);

  // Fetch filter options on component mount
  useEffect(() => {
    dispatch(fetchFilterOptions()).then((res: any) => {
      if (res.type !== 'revenueForecast/fetchFilterOptions/fulfilled') {
        rejectedApiMessage(res.payload);
      }
    });
  }, []);

  // Fetch data on component mount - only for Finance view
  useEffect(() => {
    if (activeView === 'finance' && isFinanceRole) {
      handleFinanceSearch();
    }
  }, []);

  // Fetch data when switching views - only if no data exists yet
  useEffect(() => {
    if (activeView === 'finance' && isFinanceRole && !financeInitialLoadDone.current) {
      handleFinanceSearch();
    } else if (activeView === 'sales' && isSalesRole && !salesInitialLoadDone.current) {
      handleSalesSearch();
    }
  }, [activeView]);

  const handleFinanceSearch = () => {
    const payload = {
      businessUnits: financeSelectedBU, // Empty array means ALL
      clients: financeSelectedClient, // Empty array means ALL
      departments: financeSelectedDepartment, // Empty array means ALL
      startYear: financeStartYear,
      startQuarter: financeStartQuarter,
      endYear: financeEndYear,
      endQuarter: financeEndQuarter,
      page: financePage - 1, // Backend uses 0-based indexing
      size: financePageSize,
    };

    // Set consolidated view flag based on current filter state at search time
    setFinanceShowConsolidated(financeSelectedBU.length === 0 && financeSelectedClient.length === 0);

    dispatch(fetchForecastRevenue(payload)).then((res: any) => {
      if (res.type !== 'revenueForecast/fetchForecastRevenue/fulfilled') {
        rejectedApiMessage(res.payload);
      } else {
        financeInitialLoadDone.current = true;
      }
    });
  };

  const handleFinanceLoadMore = () => {
    const nextPage = (forecastData?.page ?? 0) + 1;
    const payload = {
      businessUnits: financeSelectedBU,
      clients: financeSelectedClient,
      departments: financeSelectedDepartment,
      startYear: financeStartYear,
      startQuarter: financeStartQuarter,
      endYear: financeEndYear,
      endQuarter: financeEndQuarter,
      page: nextPage,
      size: 10,
    };
    dispatch(loadMoreForecastRevenue(payload)).then((res: any) => {
      if (res.type !== 'revenueForecast/loadMoreForecastRevenue/fulfilled') {
        rejectedApiMessage(res.payload);
      }
    });
  };

  const handleSalesSearch = () => {
    const payload = {
      clients: salesSelectedClient, // Empty array means ALL
      businessUnits: salesSelectedBU, // Empty array means ALL
      departments: salesSelectedDepartment, // Empty array means ALL
      startYear: salesStartYear,
      startQuarter: salesStartQuarter,
      endYear: salesEndYear,
      endQuarter: salesEndQuarter,
      page: salesPage - 1, // Backend uses 0-based indexing
      size: salesPageSize,
    };

    // Set consolidated view flag based on current filter state at search time
    setSalesShowConsolidated(salesSelectedBU.length === 0 && salesSelectedClient.length === 0);

    dispatch(fetchSalesViewRevenue(payload)).then((res: any) => {
      if (res.type !== 'revenueForecast/fetchSalesViewRevenue/fulfilled') {
        rejectedApiMessage(res.payload);
      } else {
        salesInitialLoadDone.current = true;
      }
    });
  };

  const handleSalesLoadMore = () => {
    const nextPage = (salesData?.page ?? 0) + 1;
    const payload = {
      businessUnits: salesSelectedBU,
      clients: salesSelectedClient,
      departments: salesSelectedDepartment,
      startYear: salesStartYear,
      startQuarter: salesStartQuarter,
      endYear: salesEndYear,
      endQuarter: salesEndQuarter,
      page: nextPage,
      size: 10,
    };
    dispatch(loadMoreSalesViewRevenue(payload)).then((res: any) => {
      if (res.type !== 'revenueForecast/loadMoreSalesViewRevenue/fulfilled') {
        rejectedApiMessage(res.payload);
      }
    });
  };

  // Calculate breakdown data from API response
  const projectsRevenue = forecastData?.['Total Forecasted Revenue (Projects + Pipeline)']?.total || 0;
  const pipelineRevenue = 0; // Will be calculated from table data if needed
  const totalRevenue = projectsRevenue;
  const actualRevenue = forecastData?.['Total Actual Revenue']?.total || 0;

  const handleFinanceReset = () => {
    setFinanceSelectedBU([]);
    setFinanceSelectedClient([]);
    setFinanceSelectedDepartment([]);
    setFinanceStartYear(currentYear);
    setFinanceStartQuarter(currentQuarter);
    setFinanceEndYear(currentYear);
    setFinanceEndQuarter(currentQuarter);
    setFinancePage(1);
  };

  const handleFinancePageChange = (page: number, pageSize: number) => {
    setFinancePage(page);
    setFinancePageSize(pageSize);
  };

  // Fetch data when finance pagination changes (only after initial load)
  useEffect(() => {
    if (financeInitialLoadDone.current && activeView === 'finance' && isFinanceRole) {
      handleFinanceSearch();
    }
  }, [financePage, financePageSize]);

  const handleSalesReset = () => {
    setSalesSelectedBU([]);
    setSalesSelectedClient([]);
    setSalesSelectedDepartment([]);
    setSalesStartYear(currentYear);
    setSalesStartQuarter(currentQuarter);
    setSalesEndYear(currentYear);
    setSalesEndQuarter(currentQuarter);
    setSalesPage(1);
  };

  const handleSalesPageChange = (page: number, pageSize: number) => {
    setSalesPage(page);
    setSalesPageSize(pageSize);
  };

  // Fetch data when sales pagination changes (only after initial load)
  useEffect(() => {
    if (salesInitialLoadDone.current && activeView === 'sales' && isSalesRole) {
      handleSalesSearch();
    }
  }, [salesPage, salesPageSize]);

  const handleFinanceExport = () => {
    // Prevent multiple simultaneous export requests
    if (exportLoading) {
      return;
    }

    const payload = {
      businessUnits: financeSelectedBU,
      clients: financeSelectedClient,
      departments: financeSelectedDepartment,
      startYear: financeStartYear,
      startQuarter: financeStartQuarter,
      endYear: financeEndYear,
      endQuarter: financeEndQuarter,
    };

    dispatch(exportFinanceViewAsExcel(payload)).then((response: any) => {
      if (response.type !== 'revenueForecast/exportFinanceViewAsExcel/fulfilled') {
        rejectedApiMessage(response.payload);
      } else {
        // Create blob and download file
        const blob = new Blob([response.payload.data], { 
          type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' 
        });
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = `Finance_Revenue_Forecast_${financeStartYear}Q${financeStartQuarter}_${financeEndYear}Q${financeEndQuarter}.xlsx`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
        
        notification.success({
          message: 'Success',
          description: 'Finance view data exported successfully',
        });
      }
    });
  };

  const handleSalesExport = () => {
    // Prevent multiple simultaneous export requests
    if (exportLoading) {
      return;
    }

    const payload = {
      clients: salesSelectedClient,
      businessUnits: salesSelectedBU,
      departments: salesSelectedDepartment,
      startYear: salesStartYear,
      startQuarter: salesStartQuarter,
      endYear: salesEndYear,
      endQuarter: salesEndQuarter,
    };

    dispatch(exportSalesViewAsExcel(payload)).then((response: any) => {
      if (response.type !== 'revenueForecast/exportSalesViewAsExcel/fulfilled') {
        rejectedApiMessage(response.payload);
      } else {
        // Create blob and download file
        const blob = new Blob([response.payload.data], { 
          type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' 
        });
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = `Sales_Revenue_Forecast_${salesStartYear}Q${salesStartQuarter}_${salesEndYear}Q${salesEndQuarter}.xlsx`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
        
        notification.success({
          message: 'Success',
          description: 'Sales view data exported successfully',
        });
      }
    });
  };

  const handleExport = () => {
    if (activeView === 'finance') {
      handleFinanceExport();
    } else {
      handleSalesExport();
    }
  };

  return (
    <div className="revenue-forecasting-page">
      {/* Loading Overlay for Export */}
      {exportLoading && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.45)',
          zIndex: 9999,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}>
          <Spin size="large" tip="Exporting data..." />
        </div>
      )}

      {/* Main Content */}
      <div className="revenue-forecasting-content-full">

        {/* Filter Drawer */}
        <FilterDrawer
          visible={filterDrawerVisible}
          onClose={() => setFilterDrawerVisible(false)}
          selectedBU={activeView === 'finance' ? financeSelectedBU : salesSelectedBU}
          selectedClient={activeView === 'finance' ? financeSelectedClient : salesSelectedClient}
          selectedDepartment={activeView === 'finance' ? financeSelectedDepartment : salesSelectedDepartment}
          startYear={activeView === 'finance' ? financeStartYear : salesStartYear}
          startQuarter={activeView === 'finance' ? financeStartQuarter : salesStartQuarter}
          endYear={activeView === 'finance' ? financeEndYear : salesEndYear}
          endQuarter={activeView === 'finance' ? financeEndQuarter : salesEndQuarter}
          onBUChange={activeView === 'finance' ? setFinanceSelectedBU : setSalesSelectedBU}
          onClientChange={activeView === 'finance' ? setFinanceSelectedClient : setSalesSelectedClient}
          onDepartmentChange={activeView === 'finance' ? setFinanceSelectedDepartment : setSalesSelectedDepartment}
          onStartYearChange={activeView === 'finance' ? setFinanceStartYear : setSalesStartYear}
          onStartQuarterChange={activeView === 'finance' ? setFinanceStartQuarter : setSalesStartQuarter}
          onEndYearChange={activeView === 'finance' ? setFinanceEndYear : setSalesEndYear}
          onEndQuarterChange={activeView === 'finance' ? setFinanceEndQuarter : setSalesEndQuarter}
          onReset={activeView === 'finance' ? handleFinanceReset : handleSalesReset}
          onSearch={activeView === 'finance' ? handleFinanceSearch : handleSalesSearch}
          businessUnits={filterOptions?.businessUnits || []}
          clients={filterOptions?.clients || []}
          departments={filterOptions?.departments || []}
          loading={filterOptionsLoading}
          activeView={activeView}
          onViewChange={setActiveView}
          showViewSelector={isAdminOrSuperuser}
        />

        {/* Main Content Area */}
        <div className="revenue-forecasting-main-full">
          {/* Loading State - only for initial Finance view load (no data yet) */}
          {activeView === 'finance' && loading && !forecastData && (
            <div style={{ textAlign: 'center', padding: '48px' }}>
              <Spin size="large" tip="Loading revenue forecast data..." />
            </div>
          )}

          {/* Error State - only for Finance view */}
          {activeView === 'finance' && !loading && error && (
            <div style={{ textAlign: 'center', padding: '48px', background: 'white', borderRadius: '8px' }}>
              <Empty
                description={
                  <span style={{ color: '#ff4d4f' }}>
                    Error loading data: {error}
                  </span>
                }
              />
            </div>
          )}

          {/* Finance View - Only accessible to Finance role */}
          {activeView === 'finance' && (forecastData || !loading) && !error && isFinanceRole && (
            <FinanceView 
              data={forecastData}
              loading={loading}
              showConsolidatedView={financeShowConsolidated}
              onOpenFilters={() => setFilterDrawerVisible(true)}
              onPageChange={handleFinancePageChange}
              currentPage={financePage}
              pageSize={financePageSize}
              totalElements={forecastData?.totalElements || 0}
            />
          )}

          {/* Loading State - only for initial Sales view load (no data yet) */}
          {activeView === 'sales' && salesLoading && !salesData && (
            <div style={{ textAlign: 'center', padding: '48px' }}>
              <Spin size="large" tip="Loading sales forecast data..." />
            </div>
          )}

          {/* Error State - only for Sales view */}
          {activeView === 'sales' && !salesLoading && salesError && (
            <div style={{ textAlign: 'center', padding: '48px', background: 'white', borderRadius: '8px' }}>
              <Empty
                description={
                  <span style={{ color: '#ff4d4f' }}>
                    Error loading data: {salesError}
                  </span>
                }
              />
            </div>
          )}

          {/* Sales View - Only accessible to Sales role */}
          {activeView === 'sales' && (salesData || !salesLoading) && !salesError && isSalesRole && (
            <SalesView 
              data={salesData}
              loading={salesLoading}
              onOpenFilters={() => setFilterDrawerVisible(true)}
              onPageChange={handleSalesPageChange}
              currentPage={salesPage}
              pageSize={salesPageSize}
              totalElements={salesData?.totalElements || 0}
            />
          )}

          {/* No Access Message */}
          {!isFinanceRole && !isSalesRole && (
            <div style={{ textAlign: 'center', padding: '48px', background: 'white', borderRadius: '8px' }}>
              <h3>Access Denied</h3>
              <p>You don't have permission to view Revenue Forecasting reports.</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default RevenueForecaste;
